import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { strategicKpisApi } from '../../../services/strategicKpisApi';
import { useToast } from '../../ui/Toast';
import type { RolloverPreview, RolloverResult, ScopeType } from '../../../types/strategicKpis';
import {
    ROLLOVER_SCOPES, ROLLOVER_SCOPE_LABEL, canStartCopy, clampRolloverYear, rolloverRows, rolloverScopeArg, rolloverTotals, rolloverYearOptions,
    targetsText, validateRolloverYears,
} from '../../../utils/strategicKpiRollover';
import { SidePanel } from '../shell/SidePanel';

interface Props {
    defaultSourceYear: number;
    defaultTargetYear: number;
    canEdit: boolean;
    /** The years the page's year filter can show; the selects offer exactly these (a later or earlier year would be a 400). */
    firstYear: number;
    maxYear: number;
    /** Called once after the real run succeeded; the page reloads the tab. */
    onDone: (result: RolloverResult) => void;
    onClose: () => void;
}

const LABEL_SIZE = { fontSize: 'var(--fs-label)', lineHeight: 'var(--lh-label)' } as const;
const ERROR_STYLE = { fontSize: 'var(--fs-label)', lineHeight: 'var(--lh-label)', color: 'var(--error)' } as const;
const SMALL_SIZE = { fontSize: 'var(--fs-small)', lineHeight: 'var(--lh-small)' } as const;

/**
 * The rollover assistant in the side panel (masterplan skpi-redesign, package C2; E5, E10): 1 Choose (years, levels),
 * 2 Preview (a SELECT-only simulation per level, reloaded whenever years or levels change), 3 Copy (real run only
 * after a confirmation step inside the panel, then the result). Mounted = open; the page (C5) owns the panel state.
 * Existing targets are never overwritten, but a new run fills every empty cell again, also targets removed since.
 * The panel is not keyed by the year: when the defaults change it resets its own choice (effect below).
 */
export function RolloverPanel({ defaultSourceYear, defaultTargetYear, canEdit, firstYear, maxYear, onDone, onClose }: Props) {
    const startSource = clampRolloverYear(defaultSourceYear, firstYear, maxYear);
    const startTarget = clampRolloverYear(defaultTargetYear, firstYear, maxYear);
    const [sourceYear, setSourceYear] = useState(startSource);
    const [targetYear, setTargetYear] = useState(startTarget);
    const [scopes, setScopes] = useState<Set<ScopeType>>(new Set(ROLLOVER_SCOPES));
    const [preview, setPreview] = useState<RolloverPreview | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [confirming, setConfirming] = useState(false);
    const [running, setRunning] = useState(false);
    const [runError, setRunError] = useState<string | null>(null);
    const [result, setResult] = useState<RolloverResult | null>(null);
    const sourceId = useId();
    const targetId = useId();
    const { toast } = useToast();
    const confirmRef = useRef<HTMLButtonElement>(null);
    const copyRef = useRef<HTMLButtonElement>(null);
    const resultRef = useRef<HTMLDivElement>(null);
    /** Set when the confirmation step went away without a result (Back, failed run): focus returns to "Copy". */
    const focusCopyNext = useRef(false);
    const runningRef = useRef(false);

    // The defaults follow the page's year: the choice starts over (no remount, so the year select keeps its focus).
    useEffect(() => {
        if (runningRef.current) return;
        setSourceYear(startSource);
        setTargetYear(startTarget);
        setScopes(new Set(ROLLOVER_SCOPES));
        setResult(null);
    }, [startSource, startTarget]);

    const yearsError = validateRolloverYears(sourceYear, targetYear);
    const scopeArg = useMemo(() => rolloverScopeArg(scopes), [scopes]);
    const scopeKey = ROLLOVER_SCOPES.filter(s => scopes.has(s)).join(',');
    const years = useMemo(() => rolloverYearOptions(firstYear, maxYear), [firstYear, maxYear]);

    // Step 2: simulation. Reloads on every change of years or levels; a stale answer is dropped.
    useEffect(() => {
        setConfirming(false);
        setRunError(null);
        setPreview(null);
        setPreviewError(null);
        if (!canEdit || yearsError !== null || result !== null) { setPreviewLoading(false); return; }
        let cancelled = false;
        setPreviewLoading(true);
        strategicKpisApi.previewRollover(sourceYear, targetYear, scopeArg)
            .then(p => { if (!cancelled) setPreview(p); })
            .catch(e => { if (!cancelled) setPreviewError(e instanceof Error ? e.message : 'The preview could not be loaded.'); })
            .finally(() => { if (!cancelled) setPreviewLoading(false); });
        return () => { cancelled = true; };
        // scopeArg follows scopeKey; result only gates the first load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceYear, targetYear, scopeKey, yearsError, canEdit]);

    // Focus follows the step: the confirm button when asking, "Copy" when the question goes away, the result when done.
    useEffect(() => {
        if (confirming) confirmRef.current?.focus();
        else if (focusCopyNext.current) { focusCopyNext.current = false; copyRef.current?.focus(); }
    }, [confirming]);
    useEffect(() => { if (result) resultRef.current?.focus(); }, [result]);

    const rows = useMemo(() => (preview ? rolloverRows(preview, scopes) : []), [preview, scopes]);
    const totals = useMemo(() => rolloverTotals(rows), [rows]);
    const copyReady = canStartCopy(canEdit, yearsError, preview, scopes) && !previewLoading;

    const toggleScope = (s: ScopeType) => setScopes(prev => {
        const next = new Set(prev);
        if (next.has(s)) { if (next.size > 1) next.delete(s); } else next.add(s);
        return next;
    });

    const run = async () => {
        if (!canEdit) return;
        runningRef.current = true;
        setRunning(true);
        setRunError(null);
        try {
            const r = await strategicKpisApi.rollover(sourceYear, targetYear, scopeArg);
            setResult(r);
            setConfirming(false);
            onDone(r);
            toast(`Rollover done: ${targetsText(r.inserted)} copied into ${r.targetYear}`, 'success');
        } catch (e) {
            const msg = e instanceof Error ? e.message : 'The rollover failed.';
            setRunError(msg);
            focusCopyNext.current = true;
            setConfirming(false);
            toast(msg, 'error');
        } finally {
            runningRef.current = false;
            setRunning(false);
        }
    };

    /** x and Escape reach this too: no closing while the copy runs. */
    const guardedClose = () => { if (!runningRef.current) onClose(); };

    const levelsText = scopes.size === ROLLOVER_SCOPES.length ? 'all levels' : ROLLOVER_SCOPES.filter(s => scopes.has(s)).map(s => ROLLOVER_SCOPE_LABEL[s]).join(', ');

    const footer = result ? (
        <button type="button" className="btn primary" onClick={guardedClose}>Close</button>
    ) : confirming ? (
        <>
            <button type="button" className="btn secondary" onClick={() => { focusCopyNext.current = true; setConfirming(false); }} disabled={running}>Back</button>
            <button type="button" className="btn primary" ref={confirmRef} onClick={() => { void run(); }} disabled={running} data-testid="rollover-confirm">
                {running && <Loader2 className="i sm animate-spin" aria-hidden="true" />}
                {running ? 'Copying…' : `Copy ${targetsText(totals.inserted)}`}
            </button>
        </>
    ) : (
        <>
            <button type="button" className="btn secondary" onClick={guardedClose}>Close</button>
            {canEdit && (
                <button type="button" className="btn primary" ref={copyRef} onClick={() => setConfirming(true)} disabled={!copyReady} data-testid="rollover-run">
                    Copy {sourceYear} → {targetYear}
                </button>
            )}
        </>
    );

    return (
        <SidePanel
            title="Roll over targets"
            sub={`Copy ${sourceYear}'s targets into ${targetYear}`}
            onClose={guardedClose}
            contentKey="rollover"
            testId="skpi-rollover-panel"
            closeLabel="Close rollover"
            footer={footer}
        >
            <p className="muted m-0" style={LABEL_SIZE}>
                Existing targets are never overwritten. A new run does fill every empty cell again, also a target someone removed after an
                earlier run; the preview shows how many. Persons who joined without a prior-year record get nothing. The C-visit target
                is calculated from the Total and is not copied.
            </p>

            {result ? (
                <div className="banner info" role="status" ref={resultRef} tabIndex={-1} data-testid="rollover-result">
                    <span className="ic"><CheckCircle2 className="i" aria-hidden="true" /></span>
                    <b>Done: {targetsText(result.inserted)} copied into {result.targetYear}</b>
                    <p className="m-0">
                        {result.personsAdded} persons added to {result.targetYear}. {result.skippedExisting} keys already set (kept as they were).
                        {' '}{result.skippedInactive} left behind (inactive persons).
                    </p>
                </div>
            ) : !canEdit ? (
                <p className="muted m-0" style={LABEL_SIZE} data-testid="rollover-no-edit">
                    Editing rights are needed. The preview asks the server the same question as the copy, so it is not offered without them.
                </p>
            ) : (
                <>
                    <section className="flex flex-col gap-3" aria-labelledby={`${sourceId}-h`}>
                        <h3 id={`${sourceId}-h`} className="strong m-0" style={LABEL_SIZE}>1 · Choose</h3>
                        <div className="flex flex-wrap items-end gap-3">
                            <label htmlFor={sourceId} className="flex flex-col gap-1" style={LABEL_SIZE}>
                                <span className="muted">From year</span>
                                <select id={sourceId} className="input" value={sourceYear} onChange={e => setSourceYear(Number(e.target.value))} data-testid="rollover-source">
                                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </label>
                            <label htmlFor={targetId} className="flex flex-col gap-1" style={LABEL_SIZE}>
                                <span className="muted">Into year</span>
                                <select id={targetId} className="input" value={targetYear} onChange={e => setTargetYear(Number(e.target.value))} data-testid="rollover-target">
                                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </label>
                        </div>
                        <fieldset className="m-0 flex flex-wrap gap-x-4 gap-y-2 border-0 p-0">
                            <legend className="muted mb-1 p-0" style={LABEL_SIZE}>Levels</legend>
                            {ROLLOVER_SCOPES.map(s => (
                                <label key={s} className="inline-flex cursor-pointer items-center gap-2" style={LABEL_SIZE}>
                                    <input type="checkbox" className="check" checked={scopes.has(s)} onChange={() => toggleScope(s)} data-testid={`rollover-scope-${s}`} />
                                    {ROLLOVER_SCOPE_LABEL[s]}
                                </label>
                            ))}
                        </fieldset>
                        {yearsError && <p className="m-0" style={ERROR_STYLE} role="alert">{yearsError}</p>}
                    </section>

                    <section className="flex flex-col gap-3" aria-labelledby={`${targetId}-h`} aria-live="polite">
                        <h3 id={`${targetId}-h`} className="strong m-0 inline-flex items-center gap-2" style={LABEL_SIZE}>
                            2 · Preview
                            {previewLoading && <Loader2 className="i sm animate-spin" aria-label="Loading the preview" />}
                        </h3>
                        {yearsError ? (
                            <p className="muted m-0" style={LABEL_SIZE}>Choose a target year after the source year to see the preview.</p>
                        ) : previewError ? (
                            <div className="banner error" role="alert">
                                <span className="ic"><AlertTriangle className="i" aria-hidden="true" /></span>
                                <b>Preview failed</b>
                                <p className="m-0">{previewError}</p>
                            </div>
                        ) : !preview ? (
                            <p className="muted m-0" style={LABEL_SIZE}>Simulating…</p>
                        ) : (
                            <>
                                <p className="m-0" style={LABEL_SIZE} data-testid="rollover-preview-summary">
                                    A simulation of {sourceYear} → {targetYear} for {levelsText}. Nothing has been written.{' '}
                                    <span className="strong">{preview.personsAdded}</span> active persons of {sourceYear} would be added to {targetYear} first.
                                </p>
                                <div className="tbl-wrap" style={{ maxHeight: 'none' }}>
                                    <table className="tbl" data-testid="rollover-preview-table">
                                        <thead>
                                            <tr>
                                                <th scope="col">Level</th>
                                                <th scope="col" className="n">Would copy</th>
                                                <th scope="col" className="n">Key already set</th>
                                                <th scope="col" className="n">Inactive person</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map(r => (
                                                <tr key={r.scope} data-testid="rollover-preview-row">
                                                    <td>{r.label}</td>
                                                    <td className="n num strong">{r.inserted}</td>
                                                    <td className="n num">{r.skippedExisting}</td>
                                                    <td className="n num">{r.skippedInactive}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td className="strong">Total</td>
                                                <td className="n num strong">{totals.inserted}</td>
                                                <td className="n num">{totals.skippedExisting}</td>
                                                <td className="n num">{totals.skippedInactive}</td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                                <ul className="muted m-0 flex list-none flex-col gap-1 p-0" style={SMALL_SIZE}>
                                    <li><span className="num">{preview.skippedInactiveKpi}</span> source targets sit on inactive KPIs and are not copied. Some of them may also count under “Key already set”, so the columns are not added together.</li>
                                    <li>“Inactive person”: targets of persons who left stay behind.</li>
                                </ul>
                                {totals.inserted === 0 && (
                                    <p className="muted m-0" style={LABEL_SIZE}>Nothing to copy for these levels.</p>
                                )}
                            </>
                        )}
                    </section>

                    <section className="flex flex-col gap-3" aria-labelledby={`${sourceId}-c`}>
                        <h3 id={`${sourceId}-c`} className="strong m-0" style={LABEL_SIZE}>3 · Copy</h3>
                        {confirming ? (
                            <div className="banner attention" role="group" aria-label="Confirm the rollover" data-testid="rollover-confirm-text">
                                <span className="ic"><AlertTriangle className="i" aria-hidden="true" /></span>
                                <b>Copy {targetsText(totals.inserted)} into {targetYear}?</b>
                                <p className="m-0">
                                    This writes to the live data: {levelsText} of {sourceYear} where {targetYear} has none yet, and adds {preview?.personsAdded ?? 0} persons.
                                    Nothing already set is overwritten, but every empty cell is filled, also one whose target was removed earlier,
                                    and every copy gets an audit row. There is no one-step undo.
                                </p>
                            </div>
                        ) : (
                            <p className="muted m-0" style={LABEL_SIZE}>
                                {copyReady ? 'Use “Copy” below, then confirm once more.' : 'Available once the preview shows something to copy.'}
                            </p>
                        )}
                        {runError && (
                            <div className="banner error" role="alert">
                                <span className="ic"><AlertTriangle className="i" aria-hidden="true" /></span>
                                <b>Rollover failed</b>
                                <p className="m-0">{runError}</p>
                            </div>
                        )}
                    </section>
                </>
            )}
        </SidePanel>
    );
}
